import React from "react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS, SCHOOL, SOCIAL } from "@/lib/constants";
import { Phone, MessageCircle, MapPin, Clock, Mail } from "lucide-react";

// Inline brand glyphs — lucide-react deprecated its brand icons, so we ship our own
// monoline SVGs to silence the deprecation warnings and stay portable.
const baseSvgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};

const FacebookIcon = (props) => (
  <svg {...baseSvgProps} {...props}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const InstagramIcon = (props) => (
  <svg {...baseSvgProps} {...props}>
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

const TikTokIcon = (props) => (
  <svg {...baseSvgProps} {...props}>
    <path d="M14 4v10.5a3.5 3.5 0 1 1-3.5-3.5" />
    <path d="M14 4a5 5 0 0 0 5 5" />
  </svg>
);

const SOCIAL_LINKS = [
  { key: "facebook", label: "Facebook", href: SOCIAL.facebook, Icon: FacebookIcon },
  { key: "instagram", label: "Instagram", href: SOCIAL.instagram, Icon: InstagramIcon },
  { key: "tiktok", label: "TikTok", href: SOCIAL.tiktok, Icon: TikTokIcon },
];

export const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="relative border-t border-maple-border bg-maple-cream2/70"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
        {/* Brand logo */}
        <div className="mb-8">
          <Logo imgClass="h-48 w-48 sm:h-64 sm:w-64" />
        </div>

        <div className="grid md:grid-cols-12 gap-10">
          {/* Tagline + contact */}
          <div className="md:col-span-5">
            <p className="text-maple-navy/75 text-sm leading-relaxed max-w-sm">
              GTA and surrounding area covered for G2 and G lessons, packages, BDE course
              options, and road-test car service.
            </p>
            <div className="mt-5 flex flex-col gap-2 text-sm">
              <a
                href={SCHOOL.phoneLink}
                className="inline-flex items-center gap-2 text-maple-navy hover:text-maple-brown transition-colors"
                data-testid="footer-call-link"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                {SCHOOL.phoneDisplay}
              </a>
              <a
                href={SCHOOL.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-maple-navy hover:text-maple-brown transition-colors"
                data-testid="footer-whatsapp-link"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                WhatsApp
              </a>
              <a
                href={SCHOOL.emailLink}
                className="inline-flex items-center gap-2 text-maple-navy hover:text-maple-brown transition-colors"
                data-testid="footer-email-link"
              >
                <Mail className="h-4 w-4" aria-hidden="true" />
                {SCHOOL.email}
              </a>
              <a
                href={SCHOOL.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-maple-navy hover:text-maple-brown transition-colors"
                data-testid="footer-maps-link"
              >
                <MapPin className="h-4 w-4 text-maple-brown" aria-hidden="true" />
                {SCHOOL.serviceArea}
              </a>
              <span className="inline-flex items-center gap-2 text-maple-navy/85">
                <Clock className="h-4 w-4 text-maple-brown" aria-hidden="true" />
                {SCHOOL.hours}
              </span>
            </div>

            {/* Social links */}
            <div className="mt-6">
              <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-maple-brown">
                Follow us
              </h4>
              <div className="mt-3 flex items-center gap-2.5" data-testid="footer-social">
                {SOCIAL_LINKS.map(({ key, label, href, Icon }) => (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Eknoor on ${label}`}
                    data-testid={`footer-social-${key}`}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-maple-border bg-white/70 text-maple-navy hover:bg-maple-tan2 hover:text-white hover:border-maple-tan2 transition-colors"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <h4 className="font-serif text-xl text-maple-navy">Explore</h4>
            <ul className="mt-4 space-y-2 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <a
                    href={`#${l.id}`}
                    className="text-maple-navy/80 hover:text-maple-brown transition-colors"
                    data-testid={`footer-link-${l.id}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Disclaimers */}
          <div className="md:col-span-4">
            <h4 className="font-serif text-xl text-maple-navy">Things to Know</h4>
            <ul className="mt-4 space-y-2 text-sm text-maple-navy/80">
              {[
                "Classes run 6 AM to 10 PM, 7 days a week",
                "Early road test date available for $50",
                "Payment by cash, debit, credit card, or Amex card",
                "All package prices are subject to applicable taxes",
                "Outside-GTA extra charge is based on distance",
                "Payment in advance and non-refundable, but transferrable to a friend or family member",
              ].map((item, i) => (
                <li key={i} className="flex gap-2 leading-relaxed">
                  <span className="text-maple-brown shrink-0" aria-hidden="true">-</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-maple-border flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <p className="text-xs text-maple-navy/70">
            © {new Date().getFullYear()} {SCHOOL.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
